import { isEqual } from "lodash";
import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

// FUTURE: Replace with https://github.com/mattrothenberg/react-overflow-list

interface UseOverflowItemsOptions {
  /**
   * Which end of the list to move into overflow first.
   * - "start" — leftmost items overflow first (e.g. breadcrumbs collapse left-to-right)
   * - "end"   — rightmost items overflow first (e.g. tab bars collapse right-to-left)
   * @default "start"
   */
  direction?: "start" | "end";
  /** Debounce delay in ms applied to the window resize handler. @default 100 */
  debounceMs?: number;

  paddingRightPx?: number;
}

/**
 * Progressively moves items into an overflow collection until the container
 * no longer overflows horizontally. Runs one item per render cycle so the DOM
 * can reflow between measurements.
 *
 * Items are compared by value (lodash `isEqual`) so passing a new array with
 * the same contents does not trigger a reset.
 */
export function useOverflowItems<T>(
  containerRef: RefObject<HTMLElement>,
  items: T[],
  { direction = "start", debounceMs = 100, paddingRightPx = 0 }: UseOverflowItemsOptions = {}
): {
  visibleItems: T[];
  overflowItems: T[];
  isCalculating: boolean;
} {
  const [overflowCount, setOverflowCount] = useState(0);
  const [isCalculating, setIsCalculating] = useState(items.length > 0);
  const [isRendering, setIsRendering] = useState(false);

  const reset = useCallback(() => {
    setOverflowCount(0);
    setIsCalculating(items.length > 0);
  }, [items.length]);

  // Compare by value so that arrays with identical contents don't trigger a reset.
  const prevItemsRef = useRef<T[]>(items);
  if (!isEqual(prevItemsRef.current, items)) {
    prevItemsRef.current = items;
    // Defer the reset to after the current render phase; calling setState
    // during render would be overwritten by the state update in useLayoutEffect
    setTimeout(reset);
  }

  // After each render: if still calculating, check whether the container overflows.
  // If it does, increment the overflow count by one and re-render to remeasure.
  // Repeats until the container fits or every item has been moved to overflow.
  useLayoutEffect(() => {
    if (!isCalculating) return;

    const container = containerRef.current;
    if (!container) return;

    // All items are already overflowing — give up.
    const allOverflowing = overflowCount >= items.length;
    if (allOverflowing) {
      setIsCalculating(false);
      return;
    }

    const { scrollWidth, clientWidth } = container;

    const isNotRenderedYet = scrollWidth === 0 && clientWidth === 0;

    // On the first render the container is not rendered yet. So width is 0.
    // In that case we want to set start rendering (setIsRendering(true)), stop there and
    // then on the next render we can do the actual overflow calculation.
    if (!isRendering && isNotRenderedYet) {
      setIsRendering(true);
      return;
    } else {
      setIsRendering(false);
    }

    const isOverflowing = scrollWidth > clientWidth;

    if (!isOverflowing) {
      if (paddingRightPx) {
        const containerRight = container.getBoundingClientRect().right;
        // Get the rightmost descendant element to check if it's fully visible, accounting for padding by only grabbing the inner most child.
        let lastChild = container.children[container.children.length - 1];
        while (lastChild instanceof HTMLElement && lastChild.children.length > 0) {
          lastChild = lastChild.children[lastChild.children.length - 1];
        }

        const lastChildRight = lastChild.getBoundingClientRect().right;
        const isLastChildVisible = lastChildRight + paddingRightPx <= containerRight;

        if (!isLastChildVisible) {
          setOverflowCount((prev) => {
            // Only increment if any items are already overflowing. Otherwise the more dropdown won't be
            // visible so it will put only one item in overflow even though its visible without the dropdown
            if (prev === 0) return 0;
            return prev + 1;
          });
        }
      }

      setIsCalculating(false);
      return;
    }

    setOverflowCount((prev) => prev + 1);
  }, [overflowCount, isCalculating, containerRef, items.length, isRendering, paddingRightPx]);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    const handleResize = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(reset, debounceMs);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [reset, debounceMs]);

  const visibleItems =
    direction === "start"
      ? items.slice(overflowCount)
      : items.slice(0, items.length - overflowCount);

  const overflowItems =
    direction === "start"
      ? items.slice(0, overflowCount)
      : items.slice(items.length - overflowCount);

  return { visibleItems, overflowItems, isCalculating };
}
